import React, {useContext, useState} from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { AuthContext } from "../contexts/AuthContext.js";
import { signUpAPI, loginAPI } from "../apis/apiAuth.js";
import { Link, useNavigate  } from "react-router-dom";
import * as Yup from "yup";
import "../styles/CreateAccount.css";
import { Eye, EyeOff } from "lucide-react";

const SignupSchema = Yup.object().shape({
  first_name: Yup.string()
    .min(2, 'Le prénom doit contenir au moins 2 caractères')
    .max(100, 'Abuse pas')
    .matches(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Le prénom ne doit contenir que des lettres')
    .required('Prénom requis'),
  last_name: Yup.string()
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .max(100, 'Abuse pas')
    .matches(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Le nom ne doit contenir que des lettres')
    .required('Nom requis'),
  mail: Yup.string()
    .email('Mail invalide')
    .required('Mail requis')
    .max(100, 'Abuse pas'),
  phone_number: Yup.string()
    .matches(
      /^[0-9]+$/,
      'Le numéro de téléphone ne doit contenir que des chiffres',
    )
    .min(10, 'Le numéro de téléphone doit contenir au moins 10 chiffres')
    .max(15, 'Le numéro de téléphone ne doit pas dépasser 15 chiffres')
    .required('Téléphone requis'),
  password: Yup.string()
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/, 
      'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial'
    )
    .required('Mot de passe requis'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Les mots de passe doivent correspondre')
    .required('Confirmation du mot de passe requise')
});

const CreateAccount = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };
    
    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const validate = (values) => {
        try {
          SignupSchema.validateSync(
            values,
            {abortEarly: false},
          );
          return true;
        } catch (validationErrors) {
          const formErrors = {};
          validationErrors.inner.forEach(error => {
            formErrors[error.path] = error.message;
          });
          console.log('handlePasswordChange', formErrors);
          return false;
        }
    };

    const handleSubmit = (values) => {
        if (validate(values)) {
        //   if (!isEnabledPolicy) {
        //     console.log('Erreur', 'Vous devez accepter les conditions générales d\'utilisations (CGU)');
        //     return;
        //   }
            values.created_at = Date.now();
          signUpAPI(values)
            .then(signupRes => {
              console.log('handleSubmit', signupRes);
              if (signupRes.message || signupRes.Message) {
                const text = signupRes.Message
                        ? signupRes.Message
                        : signupRes.message;
                console.log('Oups...', text);
              } else {
                loginAPI(values.mail, values.password)
                  .then(async loginRes => {
                    console.log('loginRes', loginRes);
                    if (loginRes.message || loginRes.Message) {
                      const text = loginRes.Message
                        ? loginRes.Message
                        : loginRes.message;
                      console.log('Oups...', text);
                    } else {
                      const userInitialized = await authCtx.initUser(
                        loginRes.IdToken,
                        loginRes.idUser,
                      );
    
                      if (userInitialized) {
                        authCtx.login(
                          loginRes.IdToken,
                          loginRes.idUser,
                          loginRes.RefreshToken,
                        );
                        localStorage.removeItem('shippingAddress');
                        navigate("/account");
                      } else {
                        console.log("Échec de l'initialisation de l'utilisateur");
                      }
                    }
                  })
                  .catch(error => {
                    console.log('loginRes error', error);
                  });
              }
            })
            .catch(error => {
              console.log('Signup error', error);
            });
        } else {
        }
    };

    return (
        <div className="form-container">
            <h1>Créer votre compte Baltee</h1>
            <p>
                Un seul compte Baltee suffit pour tous les services Baltee. <br />
                Vous avez déjà un compte Baltee ? <Link to="/login">Connectez-vous ↗</Link>
            </p>
            <Formik
                initialValues={{
                    first_name: '',
                    last_name: '',
                    mail: '',
                    phone_number: '',
                    password: '',
                    confirm_password: ''
                }}
                validationSchema={SignupSchema}
                onSubmit={(values, { setSubmitting }) => {
                    handleSubmit(values);
                    setSubmitting(false);
                }}
            >
                {({ isSubmitting }) => (
                    <Form className="apple-form">
                        <div className="form-group">
                            <Field type="text" name="first_name" placeholder="Prénom" />
                            <ErrorMessage name="first_name" component="div" className="error" />
                        </div>
                        <div className="form-group">
                            <Field type="text" name="last_name" placeholder="Nom" />
                            <ErrorMessage name="last_name" component="div" className="error" />
                        </div>
                        <div className="form-group">
                            <Field type="email" name="mail" placeholder="nom@example.com" />
                            <ErrorMessage name="mail" component="div" className="error" />
                        </div>
                        <small style={{color: '#333', fontSize: 12}}>Nous utilisons votre numéro de téléphone uniquement pour le suivi de votre livraison.</small>
                        <div className="form-group">
                            <Field type="tel" name="phone_number" placeholder="0612345678" />
                            <ErrorMessage name="phone_number" component="div" className="error" />
                        </div>
                        <div className="form-group password-field" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <Field type={showPassword ? "text" : "password"} name="password" placeholder="Mot de passe" style={{ flex: 1, paddingRight: '2.5rem' }} />
                            <button type="button" className="eye-icon" onClick={togglePasswordVisibility} style={{ position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', top: '5px' }}>
                                {showPassword ? <EyeOff /> : <Eye />}
                            </button>
                        </div>
                        <ErrorMessage name="password" component="div" className="error" style={{ marginTop: '5px' }} />
                        <div className="form-group password-field" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <Field type={showConfirmPassword ? "text" : "password"} name="confirm_password" placeholder="Confirmer le mot de passe" style={{ flex: 1, paddingRight: '2.5rem' }} />
                            <button type="button" className="eye-icon" onClick={toggleConfirmPasswordVisibility} style={{ position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', top: '5px' }}>
                                {showConfirmPassword ? <EyeOff /> : <Eye />}
                            </button>
                        </div>
                        <ErrorMessage name="confirm_password" component="div" className="error" style={{ marginTop: '5px' }} />
                        <button type="submit" className="submit-button" disabled={isSubmitting}>
                            {isSubmitting ? "Chargement..." : "Continuer"}
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default CreateAccount;